import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { computeDropdownFixedStyle } from "../utils/dropdownPosition";
import { IconMoreVertical } from "./icons";

export interface RowMenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Renders in the danger colour — destructive actions. */
  danger?: boolean;
  disabled?: boolean;
  /** Dropped from the menu entirely. For actions that depend on row state. */
  hidden?: boolean;
}

interface RowMenuProps {
  items: RowMenuItem[];
  /** Which edge of the trigger the menu lines up with. */
  align?: "left" | "right";
  ariaLabel?: string;
  /** Set on screens whose table rows are themselves clickable. */
  stopPropagation?: boolean;
  disabled?: boolean;
}

/**
 * The kebab "row actions" menu used by every list table.
 *
 * Exists because the hand-rolled copy of this markup in ~20 screens shared one
 * bug: the menu was `position: absolute`, and `.table-wrap` sets
 * `overflow-x: auto`. Per the CSS overflow spec you cannot scroll one axis and
 * leave the other visible — `overflow-y: visible` computes to `auto` — so the
 * wrapper clipped vertically too, and a menu opening downward from the LAST row
 * (or the only row) fell outside its box and vanished. `z-index` cannot help:
 * it orders painting, not clipping.
 *
 * The fix is `position: fixed` via computeDropdownFixedStyle, whose containing
 * block is the viewport rather than any scrolling ancestor — the same approach
 * products.tsx and organizations.tsx already used. That helper also flips the
 * menu upward when there is no room below, which matters precisely for the last
 * row of a table near the bottom of the window.
 *
 * Open state lives here rather than in the parent. Each screen used to carry an
 * `openMenuId`, a `menuStyle`, and a `.dropdown__backdrop` element; a document
 * listener replaces all three. Only one menu can be open at a time regardless:
 * mousedown on another trigger closes this one before that one opens.
 */
export const RowMenu = ({
  items,
  align = "right",
  ariaLabel = "Row actions",
  stopPropagation,
  disabled,
}: RowMenuProps) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const visible = items.filter((item) => !item.hidden);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // Both are checked: clicking the trigger again must fall through to its
      // own onClick (which toggles), not be treated as an outside click.
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // The menu is fixed to the viewport, so it would otherwise sit still while
    // the row beneath it scrolls away. capture:true so this fires for scrolling
    // inside an ancestor (a modal body, .table-wrap itself) and not just the
    // window.
    const reposition = () => {
      if (triggerRef.current) {
        setMenuStyle(computeDropdownFixedStyle(triggerRef.current, align));
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, align]);

  if (visible.length === 0) return null;

  return (
    <div className={`dropdown${align === "right" ? " dropdown--right" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="icon-btn"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          // Measured at click time, not on mount: the row's position depends on
          // scroll, filtering and column widths, none of which are known until
          // the moment it is opened.
          setMenuStyle(computeDropdownFixedStyle(e.currentTarget, align));
          setOpen(true);
        }}
      >
        <IconMoreVertical />
      </button>

      {open ? (
        <div ref={menuRef} className="dropdown__menu" style={menuStyle} role="menu">
          {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`dropdown__item${item.danger ? " dropdown__item--danger" : ""}`}
              disabled={item.disabled}
              onClick={(e) => {
                if (stopPropagation) e.stopPropagation();
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default RowMenu;
